const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse';
const BIGDATA_REVERSE_URL = 'https://api.bigdatacloud.net/data/reverse-geocode-client';

const reverseCache = new Map();
let reverseQueue = Promise.resolve();

const uniqueParts = (parts) => [...new Set(parts.filter(Boolean))];

/** Street / building-level address from Nominatim. */
export const buildExactPlaceLabel = (data) => {
  if (!data) return null;
  const a = data.address || {};
  const street = [a.house_number, a.road || a.pedestrian || a.footway || a.path || a.residential]
    .filter(Boolean)
    .join(' ');
  const parts = uniqueParts([
    a.building || data.name,
    street,
    a.neighbourhood || a.suburb || a.quarter || a.hamlet,
    a.village || a.town || a.city_district || a.city,
    a.county,
    a.state,
    a.postcode,
  ]);

  if (parts.length > 0) return parts.join(', ');
  return data.display_name || null;
};

/** More direct place label from BigDataCloud reverse geocode. */
export const buildDirectPlaceLabelFromBigData = (data) => {
  if (!data) return null;
  const admin = data.localityInfo?.administrative || [];
  const informative = data.localityInfo?.informative || [];
  const byOrder = [...informative, ...admin]
    .filter((item) => item?.name)
    .sort((a, b) => (b.order || 0) - (a.order || 0));

  const streetish = byOrder.find((item) =>
    /road|street|lane|avenue|nagar|colony|block|sector|highway/i.test(
      `${item.name} ${item.description || ''}`
    )
  );

  const parts = uniqueParts([
    streetish?.name,
    data.locality,
    data.city,
    data.principalSubdivision,
    data.postcode,
    data.countryName,
  ]);

  if (parts.length > 0) return parts.join(', ');
  return data.locality || data.city || data.principalSubdivision || null;
};

const cacheKey = (lat, lng) => `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;

const nominatimHeaders = {
  Accept: 'application/json',
};

const runNominatimReverse = async (lat, lng) => {
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lng),
    format: 'json',
    addressdetails: '1',
    zoom: '18',
  });

  const response = await fetch(`${NOMINATIM_REVERSE_URL}?${params.toString()}`, {
    headers: nominatimHeaders,
  });

  if (!response.ok) {
    throw new Error('Reverse geocode failed');
  }

  const data = await response.json();
  return buildExactPlaceLabel(data);
};

const runBigDataReverse = async (lat, lng) => {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    localityLanguage: 'en',
  });

  const response = await fetch(`${BIGDATA_REVERSE_URL}?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('BigData reverse geocode failed');
  }

  const data = await response.json();
  return buildDirectPlaceLabelFromBigData(data);
};

const runReverseGeocode = async (lat, lng) => {
  // Prefer BigDataCloud first — usually faster and more reliable in browsers.
  try {
    const direct = await runBigDataReverse(lat, lng);
    if (direct) return direct;
  } catch {
    // fall through
  }

  try {
    return await runNominatimReverse(lat, lng);
  } catch {
    return null;
  }
};

/** Convert lat/lng to a readable place name (cached, rate-limited). */
export const reverseGeocode = (lat, lng) => {
  if (lat == null || lng == null || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    return Promise.resolve(null);
  }

  const key = cacheKey(lat, lng);
  if (reverseCache.has(key)) {
    return Promise.resolve(reverseCache.get(key));
  }

  const task = reverseQueue.then(async () => {
    await new Promise((r) => setTimeout(r, 350));
    try {
      const label = await runReverseGeocode(lat, lng);
      reverseCache.set(key, label);
      return label;
    } catch {
      reverseCache.set(key, null);
      return null;
    }
  });

  reverseQueue = task.catch(() => {});
  return task;
};

export const prefetchLocationNames = (items = []) => {
  const seen = new Set();
  items.forEach((item) => {
    if (item?.latitude == null || item?.longitude == null) return;
    const key = cacheKey(item.latitude, item.longitude);
    if (seen.has(key) || reverseCache.has(key)) return;
    seen.add(key);
    reverseGeocode(item.latitude, item.longitude);
  });
};

export const getCachedLocationName = (lat, lng) => {
  if (lat == null || lng == null) return null;
  return reverseCache.get(cacheKey(lat, lng)) ?? null;
};

export const searchPlaces = async (query) => {
  const trimmed = query?.trim();
  if (!trimmed || trimmed.length < 2) return [];

  const params = new URLSearchParams({
    q: trimmed,
    format: 'json',
    addressdetails: '1',
    limit: '6',
  });

  const response = await fetch(`${NOMINATIM_SEARCH_URL}?${params.toString()}`, {
    headers: nominatimHeaders,
  });

  if (!response.ok) {
    throw new Error('Place search failed');
  }

  const data = await response.json();
  if (!Array.isArray(data)) return [];

  return data
    .map((item) => ({
      id: String(item.place_id),
      label: item.display_name,
      shortLabel: [item.name, item.address?.city, item.address?.state, item.address?.country]
        .filter(Boolean)
        .join(', '),
      lat: Number.parseFloat(item.lat),
      lng: Number.parseFloat(item.lon),
      type: item.type || item.class || 'place',
    }))
    .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
};
